import { FC } from 'react';

import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { GiftOrderDeliveryMode } from '@shared/api/giftOrder/giftOrder.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatPrice } from '@shared/utils/formatPrice';

export interface GiftCheckoutItemCardProps {
  itemName: string;
  itemImage?: string;
  priceRetail: number;
  quantity: number;
  minOrderQuantity: number;
  orderUnit: number;
  onIncrease: () => void;
  onDecrease: () => void;
  itemType?: GiftItemInfoType;
  deliveryMode?: GiftOrderDeliveryMode;
}

function DeliveryBadge({
  itemType,
  deliveryMode,
}: {
  itemType?: GiftItemInfoType;
  deliveryMode?: GiftOrderDeliveryMode;
}) {
  if (!itemType || itemType === GiftItemInfoType.Coupon) return null;

  const label =
    deliveryMode === GiftOrderDeliveryMode.Individual
      ? '수령자 입력 후 2~3일 내 배송'
      : '배송기간: 2~3일 소요';

  return (
    <span className="mt-1 inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-600">
      {label}
    </span>
  );
}

export const GiftCheckoutItemCard: FC<GiftCheckoutItemCardProps> =
  function GiftCheckoutItemCard({
    itemName,
    itemImage,
    priceRetail,
    quantity,
    minOrderQuantity,
    orderUnit,
    onIncrease,
    onDecrease,
    itemType,
    deliveryMode,
  }) {
    return (
      <div className="flex gap-3 border-b pb-3 last:border-b-0">
        <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded bg-gray-100">
          {itemImage ? (
            <img
              src={fileBucketUrl(itemImage, 'w200')}
              alt={itemName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
              No Image
            </div>
          )}
        </div>

        <div className="flex-1">
          <p className="text-sm font-medium">{itemName}</p>
          <p className="mt-0.5 text-sm font-bold">{formatPrice(priceRetail)}원</p>
          <DeliveryBadge itemType={itemType} deliveryMode={deliveryMode} />

          <div className="mt-2 flex items-center gap-1">
            <button
              type="button"
              onClick={onDecrease}
              disabled={quantity <= minOrderQuantity}
              className="flex h-7 w-7 items-center justify-center rounded border bg-gray-50 text-sm font-bold disabled:text-gray-300"
            >
              −
            </button>
            <span className="min-w-[32px] text-center text-sm font-bold">{quantity}</span>
            <button
              type="button"
              onClick={onIncrease}
              className="flex h-7 w-7 items-center justify-center rounded border bg-gray-50 text-sm font-bold"
            >
              +
            </button>
            {orderUnit > 1 && (
              <span className="ml-1 text-[10px] text-gray-400">({orderUnit}개 단위)</span>
            )}
          </div>
        </div>

        <div className="text-right">
          <p className="text-sm font-bold text-red-500">
            {formatPrice(priceRetail * quantity)}원
          </p>
        </div>
      </div>
    );
  };
